/**
 * @fileoverview Service for Stripe Connect API operations
 * @module stripe-connect/services/StripeConnectService
 */

import { getAuth } from 'firebase/auth';
import type {
  StripeAccountStatusResponse,
  StripeProduct,
  StripePrice,
  StripeCoupon,
  StripePaymentMethodDomain,
} from '../types';

/**
 * Get builder service API URL
 * Uses runtime hostname detection for proper URL in all environments
 */
function getApiUrl(): string {
  // First check env var (for backwards compatibility)
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }

  // Runtime detection based on hostname
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';

  // Local development → Builder Service on 8081
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }

  // Dev environment
  if (hostname.includes('devsegmentlyai')) {
    return 'https://builder-service-519070724517.us-central1.run.app';
  }

  // Production
  return 'https://api.segmently.ai';
}

/**
 * Service for interacting with Stripe Connect endpoints
 */
export class StripeConnectService {
  private apiUrl: string;

  constructor(apiUrl?: string) {
    this.apiUrl = apiUrl || getApiUrl();
  }

  /**
   * Get authorization headers for authenticated requests
   * @throws Error if user is not authenticated
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    const auth = getAuth();
    const token = await auth.currentUser?.getIdToken();

    if (!token) {
      throw new Error('User not authenticated');
    }

    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    };
  }

  /**
   * Get OAuth authorization URL to start Stripe Connect flow
   * @param projectId - Project ID
   * @param mode - OAuth mode ('test' | 'live') - determines which client_id to use
   *
   * Note: Test and live modes use different OAuth client_ids and produce different accountIds.
   * Each mode's accountId is stored separately (testAccountId, liveAccountId).
   */
  async getOAuthUrl(projectId: string, mode: 'test' | 'live'): Promise<string> {
    console.log(`[StripeConnectService] getOAuthUrl: projectId=${projectId}, mode=${mode}, apiUrl=${this.apiUrl}`);
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({
      projectId,
      mode,
    });

    const fullUrl = `${this.apiUrl}/api/stripe/oauth/url?${params}`;
    console.log(`[StripeConnectService] Fetching:`, fullUrl);
    const response = await fetch(fullUrl, { headers });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get OAuth URL' }));
      throw new Error(error.error || 'Failed to get OAuth URL');
    }

    const data = await response.json();
    return data.url;
  }

  /**
   * Get account status for a project
   * @param projectId - Project ID
   * @param mode - Optional mode override ('test' | 'live')
   * @param signal - Optional AbortSignal for request cancellation
   */
  async getAccountStatus(
    projectId: string,
    mode?: 'test' | 'live',
    signal?: AbortSignal
  ): Promise<StripeAccountStatusResponse> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    if (mode) params.append('mode', mode);

    const response = await fetch(`${this.apiUrl}/api/stripe/account?${params}`, { headers, signal });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to get account status' }));
      throw new Error(error.error || 'Failed to get account status');
    }

    return response.json();
  }

  /**
   * Disconnect Stripe account from project for a specific mode
   * @param projectId - Project ID
   * @param mode - Mode to disconnect ('test' | 'live')
   *
   * Note: Only disconnects the specified mode's account. The other mode's connection is preserved.
   */
  async disconnectAccount(projectId: string, mode: 'test' | 'live'): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/stripe/disconnect`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId, mode }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to disconnect' }));
      throw new Error(error.error || 'Failed to disconnect Stripe account');
    }
  }

  /**
   * Set Stripe mode (test/live)
   */
  async setMode(projectId: string, mode: 'test' | 'live'): Promise<void> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/stripe/mode`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId, mode }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to set mode' }));
      throw new Error(error.error || 'Failed to set Stripe mode');
    }
  }

  /**
   * List products from connected Stripe account
   * @param projectId - Project ID
   * @param mode - Optional mode override ('test' | 'live')
   * @param signal - Optional AbortSignal for request cancellation
   */
  async listProducts(
    projectId: string,
    mode?: 'test' | 'live',
    signal?: AbortSignal
  ): Promise<StripeProduct[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    if (mode) params.append('mode', mode);

    const response = await fetch(`${this.apiUrl}/api/stripe/products?${params}`, { headers, signal });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list products' }));
      throw new Error(error.error || 'Failed to list products');
    }

    const data = await response.json();
    return data.products;
  }

  /**
   * List prices for a product from connected Stripe account
   * @param projectId - Project ID
   * @param productId - Stripe Product ID
   * @param mode - Optional mode override ('test' | 'live')
   * @param signal - Optional AbortSignal for request cancellation
   */
  async listPrices(
    projectId: string,
    productId: string,
    mode?: 'test' | 'live',
    signal?: AbortSignal
  ): Promise<StripePrice[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId, productId });
    if (mode) params.append('mode', mode);

    const response = await fetch(`${this.apiUrl}/api/stripe/prices?${params}`, { headers, signal });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list prices' }));
      throw new Error(error.error || 'Failed to list prices');
    }

    const data = await response.json();
    return data.prices;
  }

  /**
   * List coupons from connected Stripe account
   * @param projectId - Project ID
   * @param mode - Optional mode override ('test' | 'live')
   * @param signal - Optional AbortSignal for request cancellation
   *
   * Note: Only returns valid (non-expired, non-deleted) coupons
   */
  async listCoupons(
    projectId: string,
    mode?: 'test' | 'live',
    signal?: AbortSignal
  ): Promise<StripeCoupon[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId });
    if (mode) params.append('mode', mode);

    const response = await fetch(`${this.apiUrl}/api/stripe/coupons?${params}`, { headers, signal });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list coupons' }));
      throw new Error(error.error || 'Failed to list coupons');
    }

    const data = await response.json();
    return data.coupons;
  }

  /**
   * List payment method domains from connected Stripe account.
   * These domains control whether Apple Pay can appear in Stripe Elements.
   */
  async listPaymentMethodDomains(
    projectId: string,
    mode: 'test' | 'live',
    signal?: AbortSignal
  ): Promise<StripePaymentMethodDomain[]> {
    const headers = await this.getAuthHeaders();
    const params = new URLSearchParams({ projectId, mode });

    const response = await fetch(`${this.apiUrl}/api/stripe/payment-method-domains?${params}`, {
      headers,
      signal,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to list payment method domains' }));
      throw new Error(error.error || 'Failed to list payment method domains');
    }

    const data = await response.json();
    return data.domains;
  }

  /**
   * Register or re-validate a payment method domain on the connected account.
   */
  async registerPaymentMethodDomain(
    projectId: string,
    mode: 'test' | 'live',
    domainName: string
  ): Promise<StripePaymentMethodDomain> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/stripe/payment-method-domains`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ projectId, mode, domainName }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to register payment method domain' }));
      throw new Error(error.error || 'Failed to register payment method domain');
    }

    const data = await response.json();
    return data.domain;
  }

  /**
   * Create a product on connected Stripe account
   * @param projectId - Project ID
   * @param name - Product name
   * @param description - Optional product description
   * @param mode - Optional mode override ('test' | 'live')
   * @param options - `confirmLive: true` is required by the server guard for
   *   any LIVE creation (P7 `assertLiveCreationAllowed`); omitted entirely for
   *   test-mode calls so the request body stays byte-identical to pre-P7.
   */
  async createProduct(
    projectId: string,
    name: string,
    description?: string,
    mode?: 'test' | 'live',
    options?: { confirmLive?: boolean }
  ): Promise<StripeProduct> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/stripe/products`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        projectId,
        name,
        description,
        mode,
        ...(options?.confirmLive === true ? { confirmLive: true } : {}),
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create product' }));
      throw new Error(error.error || 'Failed to create product');
    }

    const data = await response.json();
    return data.product;
  }

  /**
   * Create a price on connected Stripe account
   * Supports multi-currency prices via currencyOptions
   * @param projectId - Project ID
   * @param data - Price data including optional currencyOptions for multi-currency support
   * @param mode - Optional mode override ('test' | 'live')
   * @param options - `confirmLive: true` is required by the server guard for
   *   any LIVE creation (P7 `assertLiveCreationAllowed`); omitted entirely for
   *   test-mode calls so the request body stays byte-identical to pre-P7.
   * @see https://docs.stripe.com/payments/checkout/localize-prices/manual-currency-prices
   */
  async createPrice(
    projectId: string,
    data: {
      productId: string;
      unitAmount: number;
      currency: string;
      type: 'one_time' | 'recurring';
      recurring?: {
        interval: 'day' | 'week' | 'month' | 'year';
        intervalCount: number;
      };
      nickname?: string;
      /** Multi-currency support: prices in additional currencies */
      currencyOptions?: Record<string, { unitAmount: number }>;
    },
    mode?: 'test' | 'live',
    options?: { confirmLive?: boolean }
  ): Promise<StripePrice> {
    const headers = await this.getAuthHeaders();
    const response = await fetch(`${this.apiUrl}/api/stripe/prices`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        projectId,
        ...data,
        mode,
        ...(options?.confirmLive === true ? { confirmLive: true } : {}),
      }),
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Failed to create price' }));
      throw new Error(error.error || 'Failed to create price');
    }

    const priceData = await response.json();
    return priceData.price;
  }
}

// Singleton instance
let serviceInstance: StripeConnectService | null = null;

/**
 * Get singleton instance of StripeConnectService
 */
export function getStripeConnectService(): StripeConnectService {
  if (!serviceInstance) {
    serviceInstance = new StripeConnectService();
  }
  return serviceInstance;
}
